import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, theme, Badge, Grid, Drawer, Dropdown, Input, Button } from 'antd';
import { ShoppingOutlined, UserOutlined, HeartOutlined, FacebookFilled, InstagramFilled, MessageFilled, MenuOutlined, SearchOutlined, DownOutlined, LogoutOutlined, ProfileOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import zhTW from 'antd/locale/zh_TW';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// 頁面組件（僅客人前台）
import CustomOrder from './pages/CustomOrder';
import InsightDetail from './pages/InsightDetail';
import MyOrders from './pages/MyOrders';
import ShopHome from './pages/ShopHome';
import ProductDetail from './pages/ProductDetail';
import CartPage from './pages/CartPage';
import MemberProfile from './pages/MemberProfile';
import RepairApplication from './pages/RepairApplication';
import OrderDetail from './pages/OrderDetail';
import OrderConfirm from './pages/OrderConfirm';
import WishlistPage from './pages/WishlistPage';
import About from './pages/About';
import Faq from './pages/Faq';
import CrystalGuide from './pages/CrystalGuide';
import GuideArticle from './pages/GuideArticle';
import PolicyPage from './pages/PolicyPage';
import { CartProvider, useCart } from './context/CartContext';
import { CustomerProvider, useCustomer } from './context/CustomerContext';
import { SeoProvider, RouteSeo } from './seo';
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// 配置 dayjs 全局時區
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Taipei');
dayjs.locale('zh-tw');

// 商城區共用外框：桌機頂部導覽 + 右上角 ❤️🛒👤；手機底部導覽；品牌頁尾
function ShopLayout() {
  const { count, items, subtotal } = useCart();
  const { customer, isLoggedIn, login, logout } = useCustomer();
  const [cartHover, setCartHover] = useState(false); // 桌機購物車快覽
  const loc = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const BRAND = 'var(--brand-primary, #222957)';
  const active = (to) => loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to));

  // header 搜尋（電腦版）：送出後導到首頁帶 ?q=
  const doSearch = (v) => {
    const q = (v || '').trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/');
  };
  // 「關於我們」下拉：hover 出現的頁面連結
  const aboutMenu = {
    items: [
      { key: '/about', label: <Link to="/about">品牌故事</Link> },
      { key: '/crystal-guide', label: <Link to="/crystal-guide">水晶入門指南</Link> },
      { key: '/service', label: <Link to="/service">服務政策</Link> },
      { key: '/terms', label: <Link to="/terms">服務條款</Link> },
      { key: '/returns', label: <Link to="/returns">退換貨政策</Link> },
      { key: '/privacy', label: <Link to="/privacy">隱私權政策</Link> },
    ],
  };

  // 後台可改：logo、社群、頁尾標語、公告跑馬燈
  const [logo, setLogo] = useState(null);
  const [logoReady, setLogoReady] = useState(false); // logo 設定 fetch 完成前不顯示文字備援，避免閃一下
  const [social, setSocial] = useState({});
  const [tagline, setTagline] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [annBg, setAnnBg] = useState('#E9EBF5'); // 跑馬燈底色（後台可設；預設淺主色調，配主色文字）
  const [annIdx, setAnnIdx] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false); // 手機版左側抽屜
  const [searchOpen, setSearchOpen] = useState(false); // 手機版搜尋對話框
  const [knowledge, setKnowledge] = useState({ articles: [], groups: [] }); // 知識頁：分組可放上 header
  useEffect(() => {
    fetch(`${API_BASE}/api/content/knowledge`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (d) setKnowledge({ articles: Array.isArray(d.articles) ? d.articles : [], groups: Array.isArray(d.groups) ? d.groups : [] });
    }).catch(() => {});
    fetch(`${API_BASE}/api/settings/form-images`).then((r) => (r.ok ? r.json() : {})).then((d) => setLogo(d && d.logo ? d.logo : null)).catch(() => {}).finally(() => setLogoReady(true));
    fetch(`${API_BASE}/api/settings/social`).then((r) => (r.ok ? r.json() : {})).then((d) => setSocial(d || {})).catch(() => {});
    fetch(`${API_BASE}/api/settings/shop`).then((r) => (r.ok ? r.json() : {})).then((d) => {
      setTagline((d && d.shop_tagline) || '');
      setAnnouncements(Array.isArray(d && d.announcements) ? d.announcements.filter((s) => s && String(s).trim()) : []);
      if (d && d.announcement_bg) setAnnBg(d.announcement_bg);
    }).catch(() => {});
  }, []);
  // 公告每 3.5 秒往上輪播
  useEffect(() => {
    if (announcements.length <= 1) return undefined;
    const t = setInterval(() => setAnnIdx((i) => (i + 1) % announcements.length), 3500);
    return () => clearInterval(t);
  }, [announcements.length]);

  // header 底色為主色，文字/圖示一律白色；當前頁用粗體＋底線標示
  const topLink = (to, label) => (
    <Link key={to} to={to} style={{ color: '#fff', fontWeight: active(to) ? 800 : 600, fontSize: 15, textDecoration: active(to) ? 'underline' : 'none', textUnderlineOffset: 5 }}>{label}</Link>
  );
  const iconLink = (to, icon, badge) => (
    <Link key={to} to={to} style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
      <Badge count={badge || 0} size="default">{icon}</Badge>
    </Link>
  );
  const footLink = (to, l) => (
    <Link key={to} to={to} style={{ display: 'block', color: '#B9BFE5', fontSize: 13, marginBottom: 6, textDecoration: 'none' }}>{l}</Link>
  );

  // 知識頁：後台把分組「放上 Header」後，這裡各生成一個下拉；每組列出該組已發布文章＋「看全部」。
  const headerKnowledgeGroups = (knowledge.groups || [])
    .filter((g) => g && g.in_header && g.name)
    .map((g) => {
      const items = (knowledge.articles || []).filter((a) => a.group === g.name);
      return {
        name: g.name,
        menu: {
          items: [
            ...items.map((a) => ({ key: a.slug, label: <Link to={`/crystal-guide/${a.slug}`}>{a.title}</Link> })),
            { type: 'divider', key: `d-${g.name}` },
            { key: `all-${g.name}`, label: <Link to="/crystal-guide">看全部水晶知識 →</Link> },
          ],
        },
        count: items.length,
      };
    })
    .filter((g) => g.count > 0);
  const dropTrigger = (label) => (
    <span style={{ color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {label} <DownOutlined style={{ fontSize: 10 }} />
    </span>
  );

  // 會員帳號選單（依登入狀態呈現）
  const accountMenu = isLoggedIn
    ? {
        items: [
          { type: 'group', label: `Hi，${customer?.name || '會員'}`, children: [
            { key: '/member', label: <Link to="/member">會員中心</Link>, icon: <UserOutlined /> },
            { key: '/orders', label: <Link to="/orders">我的訂單</Link>, icon: <ProfileOutlined /> },
            { key: '/wishlist', label: <Link to="/wishlist">願望清單</Link>, icon: <HeartOutlined /> },
          ] },
          { type: 'divider' },
          { key: 'logout', label: '登出', icon: <LogoutOutlined />, onClick: () => { logout(); navigate('/'); } },
        ],
      }
    : {
        items: [
          { key: 'login', label: '會員登入 / 註冊', icon: <UserOutlined />, onClick: () => login('/member') },
          { type: 'divider' },
          { key: '/orders', label: <Link to="/orders">我的訂單</Link>, icon: <ProfileOutlined /> },
        ],
      };
  // 帳號觸發鈕：登入後顯示頭像＋名字（清楚呈現「已登入」），未登入顯示人頭圖示
  const accountTrigger = (
    <span style={{ color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {isLoggedIn && customer?.picture
        ? <img src={customer.picture} alt="" referrerPolicy="no-referrer"
            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.9)' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        : <UserOutlined style={{ fontSize: 24 }} />}
      {isLoggedIn && (
        <span style={{ fontSize: 13, fontWeight: 700, maxWidth: 88, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customer?.name || '會員'}
        </span>
      )}
    </span>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes annUp { from { transform: translateY(120%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      {/* 公告跑馬燈（往上輪播；後台可設多則） */}
      {announcements.length > 0 && (
        <div style={{ background: annBg, color: BRAND, fontWeight: 600, height: 34, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, letterSpacing: 0.5, padding: '0 16px' }}>
          <div key={annIdx} style={{ animation: 'annUp .5s ease', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{announcements[annIdx]}</div>
        </div>
      )}
      {/* 頂部 header（底色主色、文字白色） */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: BRAND, borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: isMobile ? '12px 16px' : '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {isMobile && (
              <button
                type="button"
                aria-label="開啟選單"
                onClick={() => setDrawerOpen(true)}
                style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: '#fff' }}
              >
                <MenuOutlined style={{ fontSize: 24 }} />
              </button>
            )}
            <Link to="/" style={{ color: '#fff', fontWeight: 800, fontSize: isMobile ? 18 : 22, letterSpacing: 2, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', minHeight: isMobile ? 30 : 40 }}>
              {!logoReady
                // 設定還沒回來：保留高度、不顯示文字備援（避免「星河耀眼」文字先閃一下再變 logo）
                ? <span style={{ display: 'inline-block', height: isMobile ? 30 : 40 }} />
                : logo
                  ? <img src={logo} alt="星河耀眼" style={{ height: isMobile ? 30 : 40, objectFit: 'contain', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                  : '星河耀眼'}
            </Link>
            {/* 桌機版：logo 右側品牌名（標語已移除） */}
            {!isMobile && (
              <Link to="/" style={{ display: 'flex', alignItems: 'center', lineHeight: 1.2, textDecoration: 'none', minWidth: 0 }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 16, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>星河耀眼 ShiningStars</span>
              </Link>
            )}
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
              {/* 搜尋（在「客製手鏈」左邊；輸入在左、放大鏡按鈕在右） */}
              <Input.Search
                allowClear
                placeholder="搜尋商品"
                enterButton={<SearchOutlined />}
                onSearch={doSearch}
                style={{ width: 220 }}
              />
              {topLink('/custom', '客製手鏈')}
              {/* 知識頁分組：後台可決定要不要放上 header */}
              {headerKnowledgeGroups.map((g) => (
                <Dropdown key={g.name} menu={g.menu} placement="bottom">
                  {dropTrigger(g.name)}
                </Dropdown>
              ))}
              {/* 關於我們：hover 展開頁面連結 */}
              <Dropdown menu={aboutMenu} placement="bottom">
                {dropTrigger('關於我們')}
              </Dropdown>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginLeft: 4 }}>
                {iconLink('/wishlist', <HeartOutlined style={{ fontSize: 24, color: '#fff' }} />)}
                {/* 購物車：hover 顯示快覽面板，最下面按鈕進購物車頁 */}
                <div
                  style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                  onMouseEnter={() => setCartHover(true)}
                  onMouseLeave={() => setCartHover(false)}
                >
                  <Link to="/cart" style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                    <Badge count={count || 0} size="default"><ShoppingOutlined style={{ fontSize: 24, color: '#fff' }} /></Badge>
                  </Link>
                  {cartHover && (
                    <div style={{ position: 'absolute', top: '100%', right: 0, paddingTop: 12, zIndex: 200 }}>
                      <div style={{ width: 340, background: '#fff', borderRadius: 12, boxShadow: '0 14px 36px rgba(34,41,87,0.20)', border: '1px solid #E5E7F5', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: '1px solid #F0F0F0', fontWeight: 800, color: '#222A5A', display: 'flex', justifyContent: 'space-between' }}>
                          <span>購物車</span><span style={{ color: '#8C8C8C', fontWeight: 600, fontSize: 13 }}>{count || 0} 件</span>
                        </div>
                        {items.length === 0 ? (
                          <div style={{ padding: '28px 16px', textAlign: 'center', color: '#8C8C8C', fontSize: 14 }}>
                            <ShoppingCartOutlined style={{ fontSize: 28, color: '#BCC1E5', display: 'block', marginBottom: 8 }} />
                            購物車是空的
                          </div>
                        ) : (
                          <>
                            <div style={{ maxHeight: 280, overflowY: 'auto', padding: '4px 0' }}>
                              {items.map((it) => (
                                <div key={it.uid || `${it.product_id}:${it.variant_id || ''}`} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 16px' }}>
                                  <div style={{ width: 44, height: 44, borderRadius: 8, flex: '0 0 auto', background: '#F2F3F9', overflow: 'hidden' }}>
                                    {it.image_url && <img src={it.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />}
                                  </div>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, color: '#222A5A', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</div>
                                    <div style={{ fontSize: 12, color: '#8C8C8C', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {it.is_custom ? '客製' : (it.spec_label || '')}{(it.spec_label || it.is_custom) ? ' · ' : ''}x{it.qty || 1}
                                    </div>
                                  </div>
                                  <div style={{ fontSize: 13, color: '#222957', fontWeight: 700, flex: '0 0 auto' }}>NT$ {(it.price || 0) * (it.qty || 1)}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ padding: '10px 16px', borderTop: '1px solid #F0F0F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#595959', fontSize: 13 }}>小計</span>
                              <span style={{ color: '#222957', fontWeight: 800, fontSize: 16 }}>NT$ {subtotal}</span>
                            </div>
                          </>
                        )}
                        <div style={{ padding: 12 }}>
                          <Button type="primary" block style={{ background: BRAND, borderColor: BRAND, height: 40, borderRadius: 10, fontWeight: 700 }}
                            onClick={() => { setCartHover(false); navigate('/cart'); }}>
                            前往購物車
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <Dropdown menu={accountMenu} placement="bottomRight">
                  {accountTrigger}
                </Dropdown>
              </div>
            </div>
          )}
          {/* 手機版右上：搜尋（放大鏡→對話框）＋購物車 */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                type="button"
                aria-label="搜尋"
                onClick={() => setSearchOpen(true)}
                style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: '#fff' }}
              >
                <SearchOutlined style={{ fontSize: 22, color: '#fff' }} />
              </button>
              {iconLink('/cart', <ShoppingOutlined style={{ fontSize: 24, color: '#fff' }} />, count)}
            </div>
          )}
        </div>
      </div>

      {/* 手機版左側抽屜導覽 */}
      <Drawer
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={260}
        styles={{ body: { padding: '8px 0' } }}
        title={<span style={{ color: BRAND, fontWeight: 800, letterSpacing: 1 }}>星河耀眼 ShiningStars</span>}
      >
        {/* 登入狀態 */}
        <div style={{ padding: '8px 24px 14px', borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
          {isLoggedIn ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {customer?.picture
                ? <img src={customer.picture} alt="" referrerPolicy="no-referrer" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${BRAND}` }} onError={(e) => { e.target.style.display = 'none'; }} />
                : <UserOutlined style={{ fontSize: 30, color: BRAND }} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#222A5A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer?.name || '會員'}</div>
                <button type="button" onClick={() => { logout(); setDrawerOpen(false); navigate('/'); }}
                  style={{ border: 'none', background: 'none', padding: 0, color: '#8C8C8C', fontSize: 13, cursor: 'pointer' }}>登出</button>
              </div>
            </div>
          ) : (
            <Button type="primary" block icon={<UserOutlined />} style={{ background: BRAND, borderColor: BRAND, height: 40, borderRadius: 10, fontWeight: 700 }}
              onClick={() => login('/member')}>會員登入 / 註冊</Button>
          )}
        </div>
        {[
          ['/custom', '客製手鏈'], ['/crystal-guide', '水晶入門指南'], ['/wishlist', '願望清單'],
          ['/cart', '購物車'], ['/member', '會員中心'], ['/orders', '我的訂單'],
          ['/about', '品牌故事'], ['/service', '服務政策'], ['/terms', '服務條款'],
          ['/returns', '退換貨政策'], ['/privacy', '隱私權政策'], ['/faq', '常見問題'],
        ].map(([to, label]) => (
          <Link
            key={to}
            to={to}
            onClick={() => setDrawerOpen(false)}
            style={{
              display: 'block', padding: '13px 24px', fontSize: 16, textDecoration: 'none',
              color: active(to) ? BRAND : '#333', fontWeight: active(to) ? 700 : 500,
              background: active(to) ? 'rgba(34,41,87,0.06)' : 'transparent',
            }}
          >
            {label}
          </Link>
        ))}
        {(social.facebook || social.instagram || social.line) && (
          <div style={{ display: 'flex', gap: 18, padding: '18px 24px', fontSize: 24, borderTop: '1px solid #f0f0f0', marginTop: 8 }}>
            {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" style={{ color: BRAND }}><FacebookFilled /></a>}
            {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" style={{ color: BRAND }}><InstagramFilled /></a>}
            {social.line && <a href={social.line} target="_blank" rel="noopener noreferrer" style={{ color: BRAND }}><MessageFilled /></a>}
          </div>
        )}
      </Drawer>

      {/* 手機版搜尋對話框（自製輕量覆蓋層，避免 antd v6 Modal 卡頓） */}
      {searchOpen && (
        <div
          onClick={() => setSearchOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', background: '#fff', padding: '14px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <Input.Search
              autoFocus
              allowClear
              size="large"
              placeholder="搜尋商品名稱"
              enterButton={<SearchOutlined />}
              onSearch={(v) => { doSearch(v); setSearchOpen(false); }}
              style={{ flex: 1 }}
            />
            <Button type="text" onClick={() => setSearchOpen(false)}>取消</Button>
          </div>
        </div>
      )}

      <div style={{ flex: 1 }}><Outlet /></div>

      {/* 品牌頁尾（手機版不顯示，改用左側抽屜導覽）；底色吃主色系 */}
      {!isMobile && (
      <footer style={{ background: `linear-gradient(160deg, ${BRAND} 0%, #232B5D 100%)`, color: '#E0E2F2', padding: '40px 32px' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: isMobile ? 'block' : 'flex', justifyContent: 'space-between', gap: 40 }}>
          <div style={{ marginBottom: isMobile ? 20 : 0 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: 2 }}>星河耀眼</div>
            <div style={{ fontSize: 13, marginTop: 8, maxWidth: 320, lineHeight: 1.7 }}>{tagline || '依五行・生命靈數，為你客製專屬能量水晶手鏈。'}</div>
            {(social.facebook || social.instagram || social.line) && (
              <div style={{ display: 'flex', gap: 14, marginTop: 14, fontSize: 22 }}>
                {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" style={{ color: '#B9BFE5' }}><FacebookFilled /></a>}
                {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" style={{ color: '#B9BFE5' }}><InstagramFilled /></a>}
                {social.line && <a href={social.line} target="_blank" rel="noopener noreferrer" style={{ color: '#B9BFE5' }}><MessageFilled /></a>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 32 : 56, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, marginBottom: 10, fontSize: 14 }}>逛逛</div>
              {footLink('/', '商城')}{footLink('/custom', '客製手鏈')}{footLink('/wishlist', '願望清單')}
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, marginBottom: 10, fontSize: 14 }}>會員</div>
              {footLink('/member', '會員中心')}{footLink('/orders', '我的訂單')}
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, marginBottom: 10, fontSize: 14 }}>關於</div>
              {footLink('/about', '品牌故事')}{footLink('/crystal-guide', '水晶入門指南')}{footLink('/service', '服務政策')}{footLink('/terms', '服務條款')}{footLink('/returns', '退換貨政策')}{footLink('/privacy', '隱私權政策')}{footLink('/faq', '常見問題')}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1240, margin: '24px auto 0', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 16, fontSize: 12, color: '#7D88CE' }}>
          © 2026 星河耀眼 ShiningStars. All rights reserved.
        </div>
      </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <ConfigProvider
      locale={zhTW}
      theme={{
        token: {
          colorPrimary: '#222957',
          colorSuccess: '#52c41a',
          colorWarning: '#faad14',
          colorError: '#ff4d4f',
          colorInfo: '#1890ff',
          borderRadius: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang TC", "Microsoft JhengHei", "Helvetica Neue", Arial, sans-serif',
        },
        algorithm: theme.defaultAlgorithm,
      }}
    >
      <CustomerProvider>
      <CartProvider>
        <SeoProvider>
        <Router>
          <RouteSeo />
          <Routes>
            {/* 沉浸式流程（不套商城導覽） */}
            <Route path="/custom" element={<CustomOrder />} />
            <Route path="/insight" element={<InsightDetail />} />
            {/* 商城區（含底部導覽） */}
            <Route element={<ShopLayout />}>
              <Route path="/" element={<ShopHome />} />
              <Route path="/shop" element={<ShopHome />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/orders" element={<MyOrders />} />
              <Route path="/orders/:orderCode" element={<OrderDetail />} />
              <Route path="/orders/:orderCode/confirm" element={<OrderConfirm />} />
              <Route path="/member" element={<MemberProfile />} />
              <Route path="/repair" element={<RepairApplication />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/service" element={<PolicyPage contentKey="service_content" titleKey="service_title" defaultTitle="服務政策" defaultContent={'感謝您選擇星河耀眼。\n\n我們提供客製與現貨能量水晶手鏈，所有商品皆由專人手作與品管。\n\n如對商品或訂單有任何問題，歡迎透過頁尾社群或客服與我們聯繫，我們會盡快協助您。'} />} />
              <Route path="/terms" element={<PolicyPage contentKey="terms_content" titleKey="terms_title" defaultTitle="服務條款" defaultContent={'歡迎使用星河耀眼（網域 shiningstars.tw，以下簡稱「本站」）。當您瀏覽、註冊、登入或下單，即表示您已閱讀並同意本服務條款。\n\n一、服務內容\n本站提供客製與現貨能量水晶手鏈之線上展示、命理配色與訂購服務。本站有權隨時新增、修改或終止全部或部分服務。\n\n二、帳號與登入\n您可使用 Google 或 LINE 帳號登入。您應對登入帳號之使用與安全負責，並確保所提供資料正確、完整。若發現帳號遭未授權使用，請立即通知本站。\n\n三、訂購與付款\n所有訂單於您完成付款後成立。商品價格、運費與付款方式以結帳頁顯示為準。金流與物流由綠界科技 ECPay 提供，您於付款時另受其服務條款規範。\n\n四、客製化商品\n客製商品為依您需求專屬製作，屬個人化商品，除本站疏失造成之瑕疵外，不適用七日猶豫期，恕不接受因個人因素之退換或取消。詳見「退換貨政策」。\n\n五、智慧財產權\n本站之商標、圖文、設計與內容均受法律保護，未經同意不得重製、散布或作商業使用。\n\n六、使用者行為\n您同意不從事違反法令、干擾服務運作、侵害他人權益，或試圖未授權存取本站系統之行為。\n\n七、免責聲明\n命理配色為文化參考與祝福用途，非醫療、投資或任何保證性建議。本站對因不可抗力或第三方服務中斷所致之損害，於法律允許範圍內不負責任。\n\n八、條款變更\n本站得隨時修訂本條款，修訂後於本頁公告即生效。若您於公告後繼續使用服務，視為同意修訂內容。\n\n九、聯絡我們\n如對本條款有任何疑問，歡迎透過頁尾社群或客服與我們聯繫。\n\n最後更新日：2026 年 7 月。'} />} />
              <Route path="/returns" element={<PolicyPage contentKey="returns_content" titleKey="returns_title" defaultTitle="退換貨政策" defaultContent={'【客製化商品重要告知】\n本商品為客製化商品，不適用消費者保護法第十九條七日猶豫期規定，請確認您的需求後再下單購買。\n\n一、現貨（非客製）商品：享有到貨後 7 天鑑賞期（非試用期），商品須保持全新完整、附件齊全。\n\n二、客製化商品：因屬專屬個人設計，除本店疏失造成之瑕疵外，恕不接受因個人因素（不喜歡、不想要）之退換或取消。\n\n三、何謂瑕疵與合理誤差：客人提供之文字、圖檔經確認後，因客人自行提供之錯字、低解析度照片導致的成果問題，不屬於瑕疵；螢幕色差（約 ±10% 內）、手工製作尺寸誤差（約 ±1 公分內）屬正常現象。\n\n四、瑕疵處理：若經判定為本店疏失之嚴重瑕疵，本店將優先提供「重新製作寄出」之服務，恕不接受整筆訂單退款。\n\n如需協助，請於期限內與我們聯繫並保留完整包裝。'} />} />
              <Route path="/privacy" element={<PolicyPage contentKey="privacy_content" titleKey="privacy_title" defaultTitle="隱私權政策" defaultContent={'星河耀眼（以下簡稱「本站」，網域 shiningstars.tw）非常重視您的個人資料保護。本政策說明我們如何蒐集、使用與保護您的資料。\n\n一、我們蒐集的資料\n．帳號登入資料：當您以 Google 或 LINE 登入時，我們會取得您授權的基本資料，包含姓名、電子郵件、頭像與帳號識別碼，用於建立與辨識您的會員身分。\n．訂單與聯絡資料：姓名、電話、收件地址、電子郵件，用於訂單處理、金流與物流。\n．命理計算資料：您自行提供的出生年月日與時辰，僅用於五行／生命靈數配色計算。\n\n二、資料的使用目的\n您的資料僅用於：會員身分辨識與登入、訂單與客服處理、出貨與付款、命理配色計算，以及依法令之必要用途。我們不會將您的個人資料販售或提供給與上述目的無關的第三方。\n\n三、資料分享對象\n為完成服務，必要時會將必要資料交由下列服務商處理：Google／LINE（登入身分驗證）、綠界科技 ECPay（金流與物流）、電子郵件寄送服務。這些服務商僅在提供服務之必要範圍內處理資料。\n\n四、Google 使用者資料\n本站對透過 Google 登入所取得資料的使用，遵循《Google API 服務使用者資料政策》（含限制使用要求）。我們僅將 Google 帳號資料用於前述登入與會員辨識，不會用於廣告，也不會轉售。\n\n五、資料保存與安全\n我們採取合理的技術與管理措施保護您的資料。命理照片等成品確認檔案僅短期保存後刪除；其餘資料保存至法令要求或服務目的完成為止。\n\n六、您的權利\n您可隨時要求查詢、更正或刪除您的個人資料，或撤回 Google／LINE 授權。如需協助，請透過頁尾社群或客服與我們聯繫。\n\n本政策如有更新將於本頁公告。最後更新日：2026 年 7 月。'} />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/crystal-guide" element={<CrystalGuide />} />
              <Route path="/crystal-guide/:slug" element={<GuideArticle />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        </SeoProvider>
      </CartProvider>
      </CustomerProvider>
    </ConfigProvider>
  );
}

export default App;
